/**
 * Attach the session token to every same-origin `/api` request.
 *
 * The API now requires an account on a set-up platform (server side:
 * middleware/require-account.js). `apiClient` has always sent the bearer token,
 * but roughly fifteen call sites reach `/api` through a bare `fetch(...)` —
 * LearningContext, CatalogConfig, DeckManager, ReviewSession, ChatThread and
 * others. Adding the header at each of them means the one that gets missed
 * turns into a broken screen in production, and the next `fetch('/api/…')`
 * anyone writes starts the problem over.
 *
 * So the token is attached in one place instead. An explicit `Authorization`
 * header always wins, so `apiClient` and the OIDC flow are untouched, and only
 * same-origin `/api` URLs are considered — nothing is ever sent to a third
 * party.
 */

/** Same-origin `/api/...` only. Relative paths, and absolute ones on our origin. */
export function isSameOriginApiUrl(url, origin) {
  if (typeof url !== 'string' || url.length === 0) return false;
  if (url.startsWith('/api/') || url === '/api') return true;
  if (!origin) return false;
  if (!url.startsWith(`${origin}/`)) return false;
  const path = url.slice(origin.length);
  return path.startsWith('/api/') || path === '/api';
}

/**
 * Wrap a fetch implementation. Exported separately from the install step so it
 * can be covered without a DOM (vitest runs in the `node` environment here).
 *
 * @param {Function} originalFetch
 * @param {() => string|null} getToken
 * @param {string} origin
 */
export function createAuthFetch(originalFetch, getToken, origin) {
  return function authFetch(input, init) {
    const url = typeof input === 'string'
      ? input
      : (input && typeof input.url === 'string' ? input.url : null);

    if (!isSameOriginApiUrl(url, origin)) return originalFetch(input, init);

    // Headers may live on `init`, or on a Request passed as `input`.
    const headers = new Headers(
      (init && init.headers) || (input && input.headers) || undefined
    );
    if (headers.has('Authorization')) return originalFetch(input, init);

    const token = getToken();
    if (!token) return originalFetch(input, init);

    headers.set('Authorization', `Bearer ${token}`);
    return originalFetch(input, { ...(init || {}), headers });
  };
}

const INSTALLED = Symbol.for('etch.authFetch.installed');

/** Idempotent: hot reload and StrictMode must not stack wrappers. */
export function installAuthFetch(target = typeof window !== 'undefined' ? window : null) {
  if (!target || typeof target.fetch !== 'function' || target[INSTALLED]) return;
  const original = target.fetch.bind(target);
  const origin = target.location?.origin || '';
  const getToken = () => {
    try {
      return target.localStorage?.getItem('etch.token') || null;
    } catch {
      return null; // private mode
    }
  };
  target.fetch = createAuthFetch(original, getToken, origin);
  target[INSTALLED] = true;
}

export default installAuthFetch;
